import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Music, DollarSign, CheckCircle, AlertCircle } from 'lucide-react';
import { SequenceFormData, UploadedFile } from '../types';
import { SpotifyTrack } from '@/services/spotifyService';

interface ReviewStepProps {
  form: UseFormReturn<SequenceFormData>;
  uploadedFile: UploadedFile | null;
  selectedTrack: SpotifyTrack | null;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  form,
  uploadedFile,
  selectedTrack,
  onSubmit,
  onBack,
  isSubmitting,
}) => {
  const formValues = form.getValues();
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    else return Math.round(bytes / 1048576) + ' MB';
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'Not specified';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review Your Sequence</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            Please review all the information below before submitting your sequence.
          </p>
        </div>

        {/* File Information */}
        <div className="space-y-3">
          <h3 className="font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            File Information
          </h3>
          <div className="pl-6 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">File Name:</span>
              <span className="font-medium">{uploadedFile?.name || 'No file'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">File Size:</span>
              <span className="font-medium">
                {uploadedFile ? formatFileSize(uploadedFile.size) : 'N/A'}
              </span>
            </div>
            {uploadedFile?.uploadedPath && (
              <div className="flex items-center text-green-600">
                <CheckCircle className="h-4 w-4 mr-1" />
                File uploaded successfully
              </div>
            )}
          </div>
        </div>

        {/* Basic Information */}
        <div className="space-y-3">
          <h3 className="font-medium">Basic Information</h3>
          <div className="pl-6 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Title:</span>
              <span className="font-medium">{formValues.title || 'Not provided'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Artist:</span>
              <span className="font-medium">{formValues.artist || 'Not provided'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Duration:</span>
              <span className="font-medium">{formatDuration(formValues.duration)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Genres:</span>
              <span className="font-medium">
                {Array.isArray(formValues.genres) && formValues.genres.length > 0
                  ? formValues.genres.join(', ')
                  : 'Not specified'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Primary Genre:</span>
              <span className="font-medium">{formValues.primary_genre || 'Not specified'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Secondary Genre:</span>
              <span className="font-medium">{formValues.secondary_genre || '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Software:</span>
              <Badge variant="secondary">{formValues.software_type}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Holiday:</span>
              <Badge variant="outline">{formValues.holiday_type}</Badge>
            </div>
          </div>
        </div>

        {/* Spotify Integration */}
        {selectedTrack && (
          <div className="space-y-3">
            <h3 className="font-medium flex items-center gap-2">
              <Music className="h-4 w-4" />
              Spotify Integration
            </h3>
            <div className="pl-6 flex items-center text-sm">
              <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
              <span>Linked to Spotify track: {selectedTrack.name}</span>
            </div>
          </div>
        )}

        {/* Pricing */}
        <div className="space-y-3">
          <h3 className="font-medium flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Pricing
          </h3>
          <div className="pl-6">
            {formValues.is_free ? (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Free Sequence
              </Badge>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Price:</span>
                  <span className="font-medium text-lg">${(formValues.price || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Your earnings (after 10% fee):</span>
                  <span className="font-medium text-green-600">
                    ${((formValues.price || 0) * 0.9).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {formValues.description && (
          <div className="space-y-3">
            <h3 className="font-medium">Description</h3>
            <div className="pl-6 text-sm text-gray-600">
              <p className="whitespace-pre-wrap">{formValues.description}</p>
            </div>
          </div>
        )}

        {/* Warning if missing required fields */}
        {(!formValues.title || !formValues.artist || !uploadedFile) && (
          <div className="p-4 bg-yellow-50 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">Missing Required Information</p>
              <ul className="list-disc list-inside space-y-1">
                {!uploadedFile && <li>Sequence file is required</li>}
                {!formValues.title && <li>Title is required</li>}
                {!formValues.artist && <li>Artist is required</li>}
              </ul>
            </div>
          </div>
        )}

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={onBack} disabled={isSubmitting}>
            Back
          </Button>
          <Button 
            onClick={onSubmit}
            disabled={isSubmitting || !formValues.title || !formValues.artist || !uploadedFile}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Sequence'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReviewStep;